import React, { useState, useEffect } from "react";
import MAuActionBar from "./MAuActionBar/ActionBar";
import DynamicTable from "../../../components/Table/MTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";
import { Upload, Loader2 } from "lucide-react";

const MAu = () => {
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 10;

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/mau/maus`;
  const { role } = useSelector((state) => state.auth);

  const columns = [
    { key: "UnitID", label: "Unit ID" },
    { key: "UnitName", label: "Unit Name" },
    { key: "Description", label: "Description / Remarks" },
  ];

  // Function to fetch all data for export
  const fetchAllUnits = async () => {
    try {
      const response = await axios.get(`${apiEndpoint}?getAllRecords=true`);
      return response.data.items;
    } catch (error) {
      console.error("Error fetching all units:", error);
      return [];
    }
  };

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'text/csv',
      'application/vnd.oasis.opendocument.spreadsheet',
      'application/x-vnd.oasis.opendocument.spreadsheet'
    ];

    if (!allowedTypes.includes(file.type)) {
      setImportError("Please upload a valid CSV or ODS file");
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    setImportError("");

    try {
      const response = await axios.post(`${apiEndpoint}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      if (response.data.message) {
        alert(response.data.message);
        await fetchUnits(1);
      }
    } catch (error) {
      console.error("Import error:", error);
      setImportError(error.response?.data?.error || "Error importing file");
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const fetchUnits = async (page = currentPage, query = searchQuery) => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${apiEndpoint}?page=${page}&limit=${itemsPerPage}&search=${query}`
      );
      setUnits(response.data.items);
      setFilteredUnits(response.data.items);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
      setTotalItems(response.data.total);
    } catch (error) {
      console.error("Error fetching units:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchUnits(page);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const searchTerm = query.toLowerCase().trim();

    if (!searchTerm) {
      setFilteredUnits(units);
      return;
    }

    const filtered = units.filter(unit => {
      const unitId = String(unit.UnitID || '').toLowerCase();
      const unitName = String(unit.UnitName || '').toLowerCase();
      const description = String(unit.Description || '').toLowerCase();

      return (
        unitId.includes(searchTerm) ||
        unitName.includes(searchTerm) ||
        description.includes(searchTerm)
      );
    });

    setFilteredUnits(filtered);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedRow) {
        await axios.put(`${apiEndpoint}/${selectedRow.UnitID}`, formData);
      } else {
        await axios.post(apiEndpoint, formData);
      }
      fetchUnits(currentPage);
      setSelectedRow(null);
    } catch (error) {
      console.error("Error saving unit:", error);
      alert(error.response?.data?.message || "Error saving unit");
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await axios.delete(`${apiEndpoint}/${selectedRow.UnitID}`);
      fetchUnits(currentPage);
      setSelectedRow(null);
    } catch (error) {
      console.error("Error deleting unit:", error);
      alert(error.response?.data?.message || "Error deleting unit");
    }
  };

  useEffect(() => {
    fetchUnits(currentPage, searchQuery);
    return () => {
      setUnits([]);
      setFilteredUnits([]);
    };
  }, [currentPage]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header exportEndpoint={apiEndpoint} />

      <div className="flex-grow p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                Accounting Units
              </h2>

              <div className="flex items-center space-x-4">
                <label className="relative cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center transition-colors">
                  <Upload className="mr-2" size={20} />
                  {importing ? "Importing..." : "Import CSV/ODS"}
                  <input
                    type="file"
                    className="hidden"
                    accept=".csv,.ods"
                    onChange={handleFileImport}
                    disabled={importing}
                  />
                </label>
                {importError && (
                  <div className="text-red-500">{importError}</div>
                )}
              </div>
            </div>

            <MAuActionBar
              title="Accounting Unit"
              rowKey="UnitID"
              deleteMessage="Are you sure you want to remove this unit permanently?"
              fields={[
                { 
                  name: "UnitName", 
                  label: "Unit", 
                  type: "text",
                  required: true 
                },
                {
                  name: "Description",
                  label: "Description / Remarks",
                  type: "text"
                },
              ]}
              apiEndpoint={apiEndpoint}
              data={filteredUnits}
              selectedRow={selectedRow}
              onRowSelect={setSelectedRow}
              onRefresh={() => fetchUnits(currentPage)}
              onSearch={handleSearch}
              handleSubmit={handleSubmit}
              handleDelete={handleDelete}
              userRole="admin"
              fetchAllData={fetchAllUnits}
            />

            <div className="mt-6">
              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="flex flex-col items-center space-y-2">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    <p className="text-gray-500">Loading units...</p>
                  </div>
                </div>
              ) : (
                <DynamicTable
                  columns={columns}
                  data={filteredUnits}
                  selectedRow={selectedRow}
                  onRowSelect={setSelectedRow}
                  rowKey="UnitID"
                  highlightColor="bg-blue-50"
                  noDataMessage="No units found."
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MAu;